import { useCallback, useEffect, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import {
  Edit as EditIcon,
  MenuBook as JournalIcon,
} from "@mui/icons-material";
import { Stack, Typography } from "@mui/material";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import { StatusEnum } from "@/features/goods-receipts";
import {
  GeneralInformationSection,
  ProductVariantsSection,
} from "@/features/goods-receipts/goods-receipt";
import { apiOutletClient, getErrorMessage } from "@/features/shared";
import {
  CenteredLoading,
  JournalDetailsDialog,
  ResponsiveToolbarButton,
} from "@/features/ui";

export const GoodsReceipt = () => {
  const { referenceNumber } = useParams();
  const navigate = useNavigate();
  const { outlet } = useAuth();
  const [loading, setLoading] = useState(true);
  const [goodsReceipt, setGoodsReceipt] = useState(null);
  const [error, setError] = useState(null);
  const [journalDialogOpen, setJournalDialogOpen] = useState(false);

  const loadGoodsReceipt = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    setError(null);
    try {
      const {
        data: { data },
      } = await apiOutletClient.get(`goods-receipts/${referenceNumber}`, {
        params: {
          withCreator: true,
          withBuyer: true,
          withSeller: true,
          withWarehouse: true,
          withProductVariants: true,
          withTransactionHistories: true,
        },
      });
      setGoodsReceipt(data);
    } catch (error) {
      setError(
        getErrorMessage(error, "Gagal memuat penerimaan barang")
      );
    } finally {
      setLoading(false);
    }
  }, [outlet, referenceNumber]);

  useEffect(() => {
    loadGoodsReceipt();
  }, [loadGoodsReceipt]);

  if (loading) {
    return <CenteredLoading sx={{ height: "100vh" }} />;
  }

  if (error) {
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );
  }

  const isEditable =
    goodsReceipt?.status === StatusEnum.RECEIVED ||
    goodsReceipt?.status === StatusEnum.PARTIALLY_INVOICED;

  const toolbarContent = (
    <>
      {isEditable && (
        <ResponsiveToolbarButton
          label="Ubah"
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() =>
            navigate(`/procurement/goods-receipts/${referenceNumber}/edit`)
          }
        />
      )}
      <ResponsiveToolbarButton
        label="Rincian Jurnal"
        variant="outlined"
        startIcon={<JournalIcon />}
        onClick={() => setJournalDialogOpen(true)}
      />
    </>
  );

  return (
    <PageContainer
      maxWidth="xl"
      title="Penerimaan Barang"
      breadcrumbs={[
        { title: "Penerimaan Barang", path: "/procurement/goods-receipts" },
        { title: referenceNumber },
      ]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
    >
      <Stack spacing={2}>
        <GeneralInformationSection goodsReceipt={goodsReceipt} />
        <ProductVariantsSection
          referenceNumber={referenceNumber}
          goodsReceiptProductVariants={goodsReceipt.goodsReceiptProductVariants}
          warehouse={goodsReceipt.warehouse}
        />

        {/* Journal Details Dialog */}
        <JournalDetailsDialog
          open={journalDialogOpen}
          onClose={() => setJournalDialogOpen(false)}
          transactionableType="GOODS_RECEIPT"
          transactionableId={goodsReceipt?.id}
        />
      </Stack>
    </PageContainer>
  );
};
